/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Technical Appendix Component (Layer 3)
 *
 * Interactive deep dives covering technical concepts from the PDF appendices.
 * Opens as an overlay without navigating away from the story.
 *
 * Topics:
 * - Modulation and cycling behavior
 * - Clean systems and efficiency
 * - Pipework, flow, and velocity
 * - Same comfort, different stress and longevity
 */

import { useState } from 'react';
import { technicalAppendixContent, type TechnicalAppendixData } from '../content/technicalAppendixContent';

interface TechnicalAppendixProps {
  appendixId: string;
  /** Close handler - required for proper cleanup */
  onClose: () => void;
}

interface InteractiveSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

function InteractiveSection({ title, children, defaultExpanded = true }: InteractiveSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className={`appendix-section ${isExpanded ? 'expanded' : 'collapsed'}`}>
      <button
        className="appendix-section-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <span className="toggle-title">{title}</span>
        <span className="toggle-indicator">{isExpanded ? '−' : '+'}</span>
      </button>
      {isExpanded && (
        <div className="appendix-section-body">
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * Modulation vs Cycling visualization (interactive version)
 */
function ModulationCyclingChart() {
  const [activeMode, setActiveMode] = useState<'modulating' | 'cycling'>('modulating');

  const W = 360;
  const H = 140;
  const PAD = { top: 16, right: 12, bottom: 24, left: 32 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;

  // Modulating curve - smooth, gradual changes
  const modulatingPath = `
    M ${PAD.left} ${PAD.top + plotH * 0.3}
    C ${PAD.left + plotW * 0.25} ${PAD.top + plotH * 0.25},
      ${PAD.left + plotW * 0.5} ${PAD.top + plotH * 0.35},
      ${PAD.left + plotW * 0.75} ${PAD.top + plotH * 0.3}
    S ${PAD.left + plotW} ${PAD.top + plotH * 0.32}
  `;

  // Cycling curve - sharp on/off pattern
  const cyclingPoints = [];
  const segments = 8;
  for (let i = 0; i <= segments; i++) {
    const x = PAD.left + (i / segments) * plotW;
    const y = i % 2 === 0 ? PAD.top + plotH * 0.1 : PAD.top + plotH * 0.7;
    cyclingPoints.push(`${i === 0 ? 'M' : 'L'} ${x} ${y}`);
    if (i < segments) {
      cyclingPoints.push(`L ${PAD.left + ((i + 0.5) / segments) * plotW} ${y}`);
      cyclingPoints.push(`L ${PAD.left + ((i + 0.5) / segments) * plotW} ${i % 2 === 0 ? PAD.top + plotH * 0.7 : PAD.top + plotH * 0.1}`);
    }
  }
  const cyclingPath = cyclingPoints.join(' ');

  return (
    <div className="modulation-cycling-chart">
      <div className="chart-controls">
        <button
          className={`chart-toggle ${activeMode === 'modulating' ? 'active' : ''}`}
          onClick={() => setActiveMode('modulating')}
        >
          Modulating
        </button>
        <button
          className={`chart-toggle ${activeMode === 'cycling' ? 'active' : ''}`}
          onClick={() => setActiveMode('cycling')}
        >
          Cycling
        </button>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="auto" className="chart-svg">
        {/* Grid */}
        <line x1={PAD.left} y1={PAD.top + plotH} x2={W - PAD.right} y2={PAD.top + plotH} stroke="#e5e7eb" strokeWidth="1" />
        <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + plotH} stroke="#e5e7eb" strokeWidth="1" />

        {/* Curves */}
        <path
          d={activeMode === 'modulating' ? modulatingPath : cyclingPath}
          fill="none"
          stroke={activeMode === 'modulating' ? '#10b981' : '#ef4444'}
          strokeWidth="2.5"
          strokeLinecap="round"
        />

        {/* Labels */}
        <text x={PAD.left - 4} y={PAD.top + 4} textAnchor="end" fontSize="9" fill="#9ca3af">100%</text>
        <text x={PAD.left - 4} y={PAD.top + plotH} textAnchor="end" fontSize="9" fill="#9ca3af">0%</text>
        <text x={PAD.left + plotW / 2} y={H - 4} textAnchor="middle" fontSize="9" fill="#9ca3af">Time →</text>
      </svg>
      <div className="chart-description">
        {activeMode === 'modulating' ? (
          <p><strong>Modulating:</strong> Smooth, continuous adjustment. Less mechanical stress, more efficient.</p>
        ) : (
          <p><strong>Cycling:</strong> Frequent on/off switching. Each startup creates wear on components.</p>
        )}
      </div>
    </div>
  );
}

/**
 * Pipework flow visualization
 */
function PipeworkFlowChart() {
  const [pipeSize, setPipeSize] = useState<'small' | 'large'>('large');

  return (
    <div className="pipework-flow-chart">
      <div className="chart-controls">
        <button
          className={`chart-toggle ${pipeSize === 'large' ? 'active' : ''}`}
          onClick={() => setPipeSize('large')}
        >
          22mm Pipe
        </button>
        <button
          className={`chart-toggle ${pipeSize === 'small' ? 'active' : ''}`}
          onClick={() => setPipeSize('small')}
        >
          10mm Pipe
        </button>
      </div>
      <div className="pipe-visualization">
        <div className={`pipe-diagram ${pipeSize}`}>
          <div className="pipe-outer">
            <div className="pipe-inner">
              <div className="flow-particles">
                {[...Array(pipeSize === 'large' ? 6 : 12)].map((_, i) => (
                  <div key={i} className="particle" style={{ animationDelay: `${i * 0.15}s` }} />
                ))}
              </div>
            </div>
          </div>
          <div className="pipe-label">
            {pipeSize === 'large' ? '22mm' : '10mm'}
          </div>
        </div>
        <div className="flow-stats">
          {pipeSize === 'large' ? (
            <>
              <div className="stat"><span className="stat-label">Velocity:</span> <span className="stat-value good">Low</span></div>
              <div className="stat"><span className="stat-label">Turbulence:</span> <span className="stat-value good">Minimal</span></div>
              <div className="stat"><span className="stat-label">Noise:</span> <span className="stat-value good">Quiet</span></div>
            </>
          ) : (
            <>
              <div className="stat"><span className="stat-label">Velocity:</span> <span className="stat-value caution">High</span></div>
              <div className="stat"><span className="stat-label">Turbulence:</span> <span className="stat-value caution">Significant</span></div>
              <div className="stat"><span className="stat-label">Noise:</span> <span className="stat-value caution">Audible</span></div>
            </>
          )}
        </div>
      </div>
      <p className="chart-note">
        Same water volume, different experience. Larger pipes allow gentler flow.
      </p>
    </div>
  );
}

/**
 * Stress vs Longevity visualization
 */
function StressLongevityChart() {
  return (
    <div className="stress-longevity-chart">
      <div className="comparison-grid">
        <div className="comparison-item steady">
          <div className="item-icon">🌊</div>
          <div className="item-title">Steady Operation</div>
          <div className="stress-bar">
            <div className="bar-fill low" style={{ width: '30%' }} />
          </div>
          <div className="item-stats">
            <span>Low stress</span>
            <span>15+ years typical life</span>
          </div>
        </div>
        <div className="comparison-item spiky">
          <div className="item-icon">⚡</div>
          <div className="item-title">Frequent Cycling</div>
          <div className="stress-bar">
            <div className="bar-fill high" style={{ width: '85%' }} />
          </div>
          <div className="item-stats">
            <span>High stress</span>
            <span>8-12 years typical life</span>
          </div>
        </div>
      </div>
      <p className="chart-note">
        Both systems deliver the same heat over time. The difference is how that work is distributed.
      </p>
    </div>
  );
}

/**
 * Clean system efficiency visualization
 */
function CleanSystemChart() {
  const [waterState, setWaterState] = useState<'clean' | 'contaminated'>('clean');

  return (
    <div className="clean-system-chart">
      <div className="chart-controls">
        <button
          className={`chart-toggle ${waterState === 'clean' ? 'active' : ''}`}
          onClick={() => setWaterState('clean')}
        >
          Clean Water
        </button>
        <button
          className={`chart-toggle ${waterState === 'contaminated' ? 'active' : ''}`}
          onClick={() => setWaterState('contaminated')}
        >
          Contaminated
        </button>
      </div>
      <div className={`system-visual ${waterState}`}>
        <div className="heat-exchanger">
          <div className="exchanger-label">Heat Exchanger</div>
          <div className="exchanger-body">
            <div className="flow-indicator" />
            {waterState === 'contaminated' && (
              <div className="scale-buildup" />
            )}
          </div>
        </div>
        <div className="efficiency-meter">
          <div className="meter-label">Efficiency</div>
          <div className="meter-bar">
            <div
              className="meter-fill"
              style={{
                width: waterState === 'clean' ? '92%' : '68%',
                background: waterState === 'clean' ? '#10b981' : '#f59e0b'
              }}
            />
          </div>
          <div className="meter-value">
            {waterState === 'clean' ? '92%' : '68%'}
          </div>
        </div>
      </div>
      <p className="chart-note">
        {waterState === 'clean'
          ? 'Clean water allows full heat transfer and smooth modulation.'
          : 'Scale and debris reduce heat transfer, forcing the system to work harder.'}
      </p>
    </div>
  );
}

export function TechnicalAppendix({ appendixId, onClose }: TechnicalAppendixProps) {
  const appendix = technicalAppendixContent[appendixId];

  if (!appendix) {
    return (
      <div className="technical-appendix-overlay">
        <div className="appendix-container">
          <div className="appendix-header">
            <h2>Technical Information</h2>
            <button className="appendix-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
          <div className="appendix-body">
            <p>This technical explainer is not yet available.</p>
          </div>
        </div>
      </div>
    );
  }

  const renderVisualization = () => {
    switch (appendix.visualizationType) {
      case 'modulation-cycling':
        return <ModulationCyclingChart />;
      case 'pipework-flow':
        return <PipeworkFlowChart />;
      case 'stress-longevity':
        return <StressLongevityChart />;
      case 'clean-system':
        return <CleanSystemChart />;
      default:
        return null;
    }
  };

  return (
    <div className="technical-appendix-overlay" onClick={(e) => {
      if (e.target === e.currentTarget) onClose();
    }}>
      <div className="appendix-container">
        <div className="appendix-header">
          <div className="header-content">
            <span className="appendix-icon">{appendix.icon}</span>
            <div>
              <h2 className="appendix-title">{appendix.title}</h2>
              <p className="appendix-subtitle">{appendix.subtitle}</p>
            </div>
          </div>
          <button className="appendix-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="appendix-body">
          <div className="appendix-intro">
            <p>{appendix.introduction}</p>
          </div>

          {/* Interactive visualization */}
          <div className="appendix-visualization">
            {renderVisualization()}
          </div>

          {/* Content sections */}
          {appendix.sections.map((section, idx) => (
            <InteractiveSection key={idx} title={section.title} defaultExpanded={idx === 0}>
              <div className="section-content">
                {section.paragraphs.map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
                {section.keyPoint && (
                  <div className="key-point">
                    <strong>Key insight:</strong> {section.keyPoint}
                  </div>
                )}
              </div>
            </InteractiveSection>
          ))}
        </div>

        <div className="appendix-footer">
          <button className="return-button" onClick={onClose}>
            ← Return to story
          </button>
        </div>
      </div>
    </div>
  );
}

export default TechnicalAppendix;
